import React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { MasterInboxReply } from '@/hooks/useMasterInboxReplies'
import { ExternalLink, RefreshCw, Tag, Send, Loader2, Mail, MessageSquare, ChevronDown, ChevronUp, Bot, UserRound, ShieldBan } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'

interface MasterInboxReplyDetailsDialogProps {
  reply: MasterInboxReply | null
  isOpen: boolean
  isAdmin?: boolean
  onClose: () => void
  onRefresh: () => void
  onViewInMasterInbox?: (reply: MasterInboxReply) => void
  onGetMessages?: (reply: MasterInboxReply) => Promise<void>
  onReCategorize?: (reply: MasterInboxReply) => Promise<void>
  onSyncToMasterInbox?: (reply: MasterInboxReply) => Promise<void>
  onTrackSDRResponse?: (reply: MasterInboxReply) => void
  onSendTestNotification?: (reply: MasterInboxReply) => Promise<void>
  isGettingMessages?: boolean
  isSendingTestNotification?: boolean
  isRecategorizing?: boolean
  isSyncingToMasterInbox?: boolean
}

export function MasterInboxReplyDetailsDialog(props: MasterInboxReplyDetailsDialogProps) {
  const {
    reply,
    isOpen,
    isAdmin = false,
    onClose,
    onRefresh,
    onViewInMasterInbox,
    onGetMessages,
    onReCategorize,
    onSyncToMasterInbox,
    onTrackSDRResponse,
    onSendTestNotification,
    isGettingMessages = false,
    isSendingTestNotification = false,
    isRecategorizing = false,
    isSyncingToMasterInbox = false,
  } = props

  // State to track collapsed/expanded messages
  const [collapsedMessages, setCollapsedMessages] = React.useState<Set<string>>(new Set())

  // Blocklist status for this reply's email
  const [blocklistEntry, setBlocklistEntry] = React.useState<{
    id: string
    block_type: string
    block_reason: string
    smartlead_sync_status: string
    expires_at: string | null
    expired: boolean
    created_at: string
  } | null>(null)
  const [blocklistLoading, setBlocklistLoading] = React.useState(false)

  // Fetch blocklist status when dialog opens
  React.useEffect(() => {
    if (!isOpen || !reply?.email) {
      setBlocklistEntry(null)
      return
    }

    const fetchBlocklistStatus = async () => {
      setBlocklistLoading(true)
      try {
        const { data } = await (supabase as any)
          .from('blocklist')
          .select('id, block_type, block_reason, smartlead_sync_status, expires_at, expired, created_at')
          .eq('email', reply.email)
          .eq('expired', false)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        setBlocklistEntry(data || null)
      } catch (err) {
        console.error('Error fetching blocklist status:', err)
        setBlocklistEntry(null)
      } finally {
        setBlocklistLoading(false)
      }
    }

    fetchBlocklistStatus()
  }, [isOpen, reply?.email])

  /**
   * Extract embedded outbound quote from a reply message's HTML or text.
   * When the MasterInbox API returns only 1 message (the reply) with no sent:1 messages,
   * the reply may contain the original outbound email quoted via:
   *
   * HTML-based detection (highest priority):
   * - .gmail_quote class (Gmail)
   * - <blockquote> (Apple Mail, Outlook HTML)
   *
   * Text-based detection (fallback when no HTML quotes):
   * - -----Original Message----- / --- original message --- (Outlook)
   * - "On ... wrote:" pattern with newlines (proper plain text)
   * - "On ... wrote: >" flattened pattern (Gmail API flattens newlines)
   * - "From: ... Date: ... Subject:" email header block
   * - "-- Please reply above this line --" (help desk)
   * - Pure chevron "> " quoting (short reply followed by > quoted block)
   * - ---------- Forwarded message --------- (Gmail forward)
   *
   * Returns { replyContent, quotedOutbound } if a quote is found, or null if no embedded quote detected.
   */
  const extractEmbeddedOutbound = (htmlContent: string, textContent: string): {
    replyHtml: string
    replyText: string
    quotedOutboundHtml: string
    quotedOutboundText: string
    quotedSenderInfo: string
  } | null => {
    // ========== HTML-BASED STRATEGIES (highest priority) ==========
    if (htmlContent) {
      // Strategy 1: Gmail-style <div class="gmail_quote">
      const gmailQuoteRegex = /<div\s+class="gmail_quote"[^>]*>([\s\S]*?)$/i
      const gmailMatch = htmlContent.match(gmailQuoteRegex)
      if (gmailMatch) {
        const splitIndex = htmlContent.indexOf(gmailMatch[0])
        const replyHtml = htmlContent.substring(0, splitIndex).trim()
        const quotedHtml = gmailMatch[0]
        // Try to extract sender info from "On ... wrote:" line before the gmail_quote div
        const wroteLineRegex = /(?:<div[^>]*>)?\s*((?:On|Den|El|Le|Am)\s+.+?\s+(?:wrote|skrev|escribió|a écrit|schrieb)\s*:\s*)<\/div>\s*$/i
        const wroteBefore = replyHtml.match(wroteLineRegex)
        let finalReplyHtml = replyHtml
        let senderInfo = ''
        if (wroteBefore) {
          finalReplyHtml = replyHtml.substring(0, replyHtml.lastIndexOf(wroteBefore[0])).trim()
          senderInfo = wroteBefore[1].replace(/<[^>]*>/g, '').trim()
        }
        return {
          replyHtml: finalReplyHtml,
          replyText: '',
          quotedOutboundHtml: quotedHtml,
          quotedOutboundText: '',
          quotedSenderInfo: senderInfo
        }
      }

      // Strategy 2: Apple Mail / generic <blockquote type="cite"> or just <blockquote>
      const blockquoteRegex = /<blockquote[^>]*>([\s\S]*?)$/i
      const blockquoteMatch = htmlContent.match(blockquoteRegex)
      if (blockquoteMatch) {
        const splitIndex = htmlContent.indexOf(blockquoteMatch[0])
        let replyHtml = htmlContent.substring(0, splitIndex).trim()
        const quotedHtml = blockquoteMatch[0]
        // Try to extract "wrote:" line right before blockquote
        let senderInfo = ''
        const wroteBeforeBlockquote = replyHtml.match(/((?:Den|On|El|Le|Am)\s+.+?\s+(?:skrev|wrote|escribió|a écrit|schrieb)\s*.*?:)\s*(?:<br\s*\/?>)?\s*$/i)
        if (wroteBeforeBlockquote) {
          senderInfo = wroteBeforeBlockquote[1].replace(/<[^>]*>/g, '').trim()
          replyHtml = replyHtml.substring(0, replyHtml.lastIndexOf(wroteBeforeBlockquote[0])).trim()
        }
        return {
          replyHtml,
          replyText: '',
          quotedOutboundHtml: quotedHtml,
          quotedOutboundText: '',
          quotedSenderInfo: senderInfo
        }
      }
    }

    // ========== TEXT-BASED STRATEGIES (fallback) ==========
    if (textContent) {
      // Strategy 3: -----Original Message----- (Outlook standard)
      const outlookMarker = '-----Original Message-----'
      const outlookIdx = textContent.indexOf(outlookMarker)
      if (outlookIdx !== -1) {
        return {
          replyHtml: '',
          replyText: textContent.substring(0, outlookIdx).trim(),
          quotedOutboundHtml: '',
          quotedOutboundText: textContent.substring(outlookIdx + outlookMarker.length).trim(),
          quotedSenderInfo: 'Original Message'
        }
      }

      // Strategy 4: --- original message --- (loose variant, e.g. mobile clients)
      const looseOriginalRegex = /---+\s*original\s*message\s*---+/i
      const looseOriginalMatch = textContent.match(looseOriginalRegex)
      if (looseOriginalMatch && looseOriginalMatch.index !== undefined) {
        const markerEnd = looseOriginalMatch.index + looseOriginalMatch[0].length
        return {
          replyHtml: '',
          replyText: textContent.substring(0, looseOriginalMatch.index).trim(),
          quotedOutboundHtml: '',
          quotedOutboundText: textContent.substring(markerEnd).trim(),
          quotedSenderInfo: 'Original Message'
        }
      }

      // Strategy 5: "-- Please reply above this line --" (help desk / ticketing systems)
      const replyAboveRegex = /--\s*Please reply above this line\s*--/i
      const replyAboveMatch = textContent.match(replyAboveRegex)
      if (replyAboveMatch && replyAboveMatch.index !== undefined) {
        const markerEnd = replyAboveMatch.index + replyAboveMatch[0].length
        const afterMarker = textContent.substring(markerEnd).trim()
        if (afterMarker.length > 20) {
          return {
            replyHtml: '',
            replyText: textContent.substring(0, replyAboveMatch.index).trim(),
            quotedOutboundHtml: '',
            quotedOutboundText: afterMarker,
            quotedSenderInfo: 'Original Thread'
          }
        }
      }

      // Strategy 6: "On ... wrote:" with proper newlines (multi-line plain text)
      const wroteRegex = /\n\s*((?:On|Den|El|Le|Am)\s+.+?\s+(?:wrote|skrev|escribió|a écrit|schrieb)\s*:)\s*\n/i
      const wroteMatch = textContent.match(wroteRegex)
      if (wroteMatch && wroteMatch.index !== undefined) {
        const splitPoint = wroteMatch.index
        const afterWrote = splitPoint + wroteMatch[0].length
        const quotedPart = textContent.substring(afterWrote).trim()
        // Clean up chevron-quoted lines: remove leading >
        const cleanedQuoted = quotedPart
          .split('\n')
          .map((line: string) => line.replace(/^>\s?/, ''))
          .join('\n')
          .trim()
        if (cleanedQuoted.length > 10) {
          return {
            replyHtml: '',
            replyText: textContent.substring(0, splitPoint).trim(),
            quotedOutboundHtml: '',
            quotedOutboundText: cleanedQuoted,
            quotedSenderInfo: wroteMatch[1].replace(/<[^>]*>/g, '').trim()
          }
        }
      }

      // Strategy 7: Flattened "On ... wrote: >" pattern (Gmail API strips newlines)
      // Matches: "reply text On Mon, Jan 26, 2026 at 2:07 PM Name wrote: > quoted text"
      // Also: "reply text On Mon, Jan 26, 2026 at 2:07 PM Name <email> wrote: > quoted"
      const flatWroteRegex = /\s((?:On|Den|El|Le|Am)\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun|Man|Tir|Ons|Tor|Fre|Lør|Søn|\w{3,9})[^>]*?(?:wrote|skrev|escribió|a écrit|schrieb)\s*:\s*)>\s/i
      const flatWroteMatch = textContent.match(flatWroteRegex)
      if (flatWroteMatch && flatWroteMatch.index !== undefined) {
        const splitPoint = flatWroteMatch.index
        const afterWrote = splitPoint + flatWroteMatch[0].length - 2 // back up before "> "
        const quotedRaw = textContent.substring(afterWrote).trim()
        // Clean flattened chevron quoting: split on " > " boundaries to reconstruct lines
        // In flattened text: "> line1 > line2 > > line3" means line1, line2, blank, line3
        const cleanedQuoted = quotedRaw
          .replace(/\s*>\s*>\s*/g, '\n\n')  // " > > " = blank line between paragraphs
          .replace(/\s*>\s*/g, '\n')          // " > " = line break
          .replace(/^\n+/, '')                 // trim leading newlines
          .trim()
        if (cleanedQuoted.length > 10) {
          return {
            replyHtml: '',
            replyText: textContent.substring(0, splitPoint).trim(),
            quotedOutboundHtml: '',
            quotedOutboundText: cleanedQuoted,
            quotedSenderInfo: flatWroteMatch[1].replace(/<[^>]*>/g, '').trim()
          }
        }
      }

      // Strategy 8: "From: ... Date: ... Subject:" email header block (Apple Mail, some Outlook)
      // Matches forwarded-style headers without the "Forwarded message" marker
      const emailHeaderRegex = /\s(From:\s*.+?\s*>?\s*Date:\s*.+?\s*(?:To:\s*.+?\s*)?Subject:\s*)/i
      const emailHeaderMatch = textContent.match(emailHeaderRegex)
      if (emailHeaderMatch && emailHeaderMatch.index !== undefined) {
        const splitPoint = emailHeaderMatch.index
        const afterHeaders = splitPoint + emailHeaderMatch[0].length
        const quotedPart = textContent.substring(afterHeaders).trim()
        if (quotedPart.length > 20) {
          // Extract sender from "From:" line
          const fromMatch = emailHeaderMatch[1].match(/From:\s*(.+?)(?:\s*>?\s*Date:)/i)
          const senderInfo = fromMatch ? fromMatch[1].replace(/<[^>]*>/g, '').trim() : 'Forwarded'
          return {
            replyHtml: '',
            replyText: textContent.substring(0, splitPoint).trim(),
            quotedOutboundHtml: '',
            quotedOutboundText: quotedPart,
            quotedSenderInfo: `From: ${senderInfo}`
          }
        }
      }

      // Strategy 9: ---------- Forwarded message --------- (Gmail forward)
      const forwardMarker = '---------- Forwarded message ---------'
      const forwardIdx = textContent.indexOf(forwardMarker)
      if (forwardIdx !== -1) {
        return {
          replyHtml: '',
          replyText: textContent.substring(0, forwardIdx).trim(),
          quotedOutboundHtml: '',
          quotedOutboundText: textContent.substring(forwardIdx + forwardMarker.length).trim(),
          quotedSenderInfo: 'Forwarded Message'
        }
      }

      // Strategy 10: Pure chevron ">" quoting in flattened text (no "wrote:" marker)
      // Pattern: short reply text followed by "> > > Hey Name," or "> Hey Name,"
      // Only trigger if: reply is short (< 200 chars before first ">") AND quoted block is substantial
      const pureChevronRegex = /^(.{1,200}?)\s+((?:>\s*){1,5}Hey\s)/i
      const pureChevronMatch = textContent.match(pureChevronRegex)
      if (pureChevronMatch && pureChevronMatch.index !== undefined) {
        const replyPart = pureChevronMatch[1].trim()
        const chevronStart = pureChevronMatch.index + pureChevronMatch[1].length
        const quotedRaw = textContent.substring(chevronStart).trim()
        // Clean flattened chevron quoting
        const cleanedQuoted = quotedRaw
          .replace(/\s*>\s*>\s*/g, '\n\n')
          .replace(/\s*>\s*/g, '\n')
          .replace(/^\n+/, '')
          .trim()
        if (cleanedQuoted.length > 30 && replyPart.length > 0) {
          return {
            replyHtml: '',
            replyText: replyPart,
            quotedOutboundHtml: '',
            quotedOutboundText: cleanedQuoted,
            quotedSenderInfo: 'Quoted Original'
          }
        }
      }
    }

    return null
  }

  /**
   * Strip HTML tags and decode entities to produce plain text
   */
  const htmlToPlainText = (html: string): string => {
    return html
      .replace(/<div[^>]*>/gi, '\n')
      .replace(/<\/div>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<p[^>]*>/gi, '\n')
      .replace(/<\/p>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  }

  if (!reply || !isOpen) return null

  // Safety check - ensure reply has required fields
  if (!reply.id) {
    console.error('MasterInboxReplyDetailsDialog: reply missing id', reply)
    return null
  }

  const handleGetMessagesClick = async () => {
    try {
      await onGetMessages(reply)
    } catch (error) {
      console.error('Error getting messages:', error)
    }
  }

  const handleSendTestNotificationClick = async () => {
    if (!onSendTestNotification) return
    try {
      await onSendTestNotification(reply)
    } catch (error) {
      console.error('Error sending test notification:', error)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle>Master Inbox Reply Details</DialogTitle>
              <DialogDescription>
                Complete information for this Master Inbox reply
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Data
              </Button>

              {reply.thread_url && onViewInMasterInbox && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewInMasterInbox(reply)}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  View in MasterInbox
                </Button>
              )}

              {onGetMessages && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGetMessagesClick}
                  disabled={isGettingMessages || !reply.prospect_id}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isGettingMessages ? 'animate-spin' : ''}`} />
                  {isGettingMessages ? 'Getting Messages...' : 'Get Messages'}
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-col flex-1 min-h-0">
          <Tabs defaultValue="overview" className="flex flex-col flex-1">
            <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="messages">Messages</TabsTrigger>
              <TabsTrigger value="other">Other</TabsTrigger>
            </TabsList>

            <div className="flex-1 min-h-0 mt-4">
              <ScrollArea className="h-[calc(90vh-200px)] w-full">
                <div className="w-full pr-4 space-y-6">
                  <TabsContent value="overview" className="space-y-6 w-full">
                    {/* Lead / Prospect + Sender Summary */}
                    <div className="bg-white border rounded-lg p-0 overflow-hidden">
                      {/* Top identities row */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-gradient-to-r from-slate-50 to-slate-100">
                        {/* Lead / Prospect: from_name/from_address for both email and LinkedIn */}
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center text-lg font-semibold shadow-sm">
                            {(reply.from_name || reply.name || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs text-muted-foreground mb-1">
                              Lead / prospect
                            </div>
                            <div className="text-base font-semibold truncate">
                              {reply.from_name || reply.name || 'Unknown Prospect'}
                            </div>
                            <div className="text-xs text-slate-600 truncate">
                              {reply.channel_id?.toLowerCase().includes('heyreach')
                                ? (reply.linkedin || reply.from_address || 'No LinkedIn available')
                                : (reply.email || reply.from_address || 'No email available')}
                            </div>
                          </div>
                        </div>

                        {/* Sender: to_name/to_address for both email and LinkedIn */}
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-lg font-semibold shadow-sm">
                            {(reply.to_name || reply.channel || '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs text-muted-foreground mb-1">
                              Sender
                            </div>
                            <div className="text-base font-semibold truncate">
                              {reply.to_name || reply.workspace_name || 'Unknown Sender'}
                            </div>
                            <div className="text-xs text-slate-600 truncate">
                              {reply.to_address || reply.channel || 'No sender address'}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Conversation stats */}
                      <div className={`grid grid-cols-1 ${reply.channel_id?.toLowerCase().includes('heyreach') ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4 px-6 py-4 border-t bg-white text-sm`}>
                        <div>
                          <div className="text-xs text-muted-foreground">
                            Conversation stage
                          </div>
                          <div className="mt-1 font-medium text-slate-900">
                            {reply.conversation_stage || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">
                            Message quantity
                          </div>
                          <div className="mt-1 font-medium text-slate-900">
                            {reply.message_quantity != null ? reply.message_quantity : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">
                            Has outbound message
                          </div>
                          <div className="mt-1 font-medium text-slate-900">
                            {reply.has_outbound_message === true ? 'true' : 'false'}
                          </div>
                        </div>
                        {reply.channel_id?.toLowerCase().includes('heyreach') && (
                          <div>
                            <div className="text-xs text-muted-foreground">
                              Reply count
                            </div>
                            <div className="mt-1 font-medium text-slate-900">
                              {reply.reply_count != null ? (
                                <Badge variant="outline" className={
                                  reply.reply_count === 1 ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                  reply.reply_count === 2 ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                  'bg-orange-50 text-orange-700 border-orange-200'
                                }>
                                  {reply.reply_count === 1 ? '1st Reply' :
                                   reply.reply_count === 2 ? '2nd Reply' :
                                   `${reply.reply_count}th Reply (Follow-up)`}
                                </Badge>
                              ) : 'N/A'}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Reply Categorization */}
                    <div className="bg-white border rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-900">Reply Categorization</h4>
                        {onReCategorize && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onReCategorize(reply)}
                            disabled={isRecategorizing}
                          >
                            {isRecategorizing ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Categorizing...
                              </>
                            ) : (
                              <>
                                <Tag className="w-4 h-4 mr-2" />
                                {reply.label_name ? 'Re-categorize' : 'Categorize'}
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                      {!reply.label_name && (
                        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                          <p className="text-sm text-muted-foreground">
                            This reply has not been categorized yet. Click "Categorize" to categorize it using AI.
                          </p>
                        </div>
                      )}
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Response Category:</span>
                          <span className="font-medium">{reply.label_name || 'Uncategorized'}</span>
                        </div>
                        {isAdmin && <div className="flex justify-between">
                          <span className="text-gray-600">Category ID:</span>
                          <span className="font-medium">{reply.label_id || 'N/A'}</span>
                        </div>}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Reply-Type:</span>
                          {reply.reply_category_type ? (() => {
                            const normalizedType = reply.reply_category_type.toLowerCase()
                            let colorClass = ''
                            
                            // Badge colors for reply category types
                            if (normalizedType === 'positive') {
                              colorClass = 'bg-green-100 text-green-800 border-green-300'
                            } else if (normalizedType === 'negative') {
                              colorClass = 'bg-red-100 text-red-800 border-red-300'
                            } else if (normalizedType === 'ooo_bounced' || normalizedType === 'ooo' || normalizedType === 'auto-reply' || normalizedType === 'autoreply' || normalizedType === 'out of office') {
                              colorClass = 'bg-purple-100 text-purple-800 border-purple-300'
                            } else if (normalizedType === 'ai_messup' || normalizedType === 'neutral') {
                              colorClass = 'bg-orange-100 text-orange-800 border-orange-300'
                            }
                            
                            return (
                              <Badge 
                                variant="outline"
                                className={`${colorClass}`}
                              >
                                {reply.reply_category_type}
                              </Badge>
                            )
                          })() : (
                            <span className="font-medium text-gray-500">N/A</span>
                          )}
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sub Label:</span>
                          {reply.sub_label ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {reply.sub_label}
                            </Badge>
                          ) : (
                            <span className="font-medium text-gray-500">N/A</span>
                          )}
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Reply Origin:</span>
                          {reply.is_human_reply !== null && reply.is_human_reply !== undefined ? (
                            <Badge variant="outline" className={`${reply.is_human_reply ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                              {reply.is_human_reply ? <UserRound className="h-3 w-3 mr-1" /> : <Bot className="h-3 w-3 mr-1" />}
                              {reply.is_human_reply ? 'Human Reply' : 'Automatic Reply'}
                            </Badge>
                          ) : (
                            <span className="font-medium text-gray-500">N/A</span>
                          )}
                        </div>
                        {isAdmin && (<>
                        <div className="flex justify-between">
                          <span className="text-gray-600">AI Model Used:</span>
                          <span className="font-medium">{reply.categorization_model || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Categorized At:</span>
                          <span className="font-medium">
                            {reply.categorized_at ? new Date(reply.categorized_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">MasterInbox Sync:</span>
                          <span className={`font-medium ${
                            reply.masterinbox_sync_status === 'success' ? 'text-green-600' :
                            reply.masterinbox_sync_status === 'failed' || reply.masterinbox_sync_status === 'error' ? 'text-red-600' :
                            reply.masterinbox_sync_status === 'skipped' ? 'text-yellow-600' :
                            'text-gray-500'
                          }`}>
                            {reply.masterinbox_sync_status === 'success' ? '✅ Synced' :
                             reply.masterinbox_sync_status === 'failed' ? '❌ Failed' :
                             reply.masterinbox_sync_status === 'error' ? '❌ Error' :
                             reply.masterinbox_sync_status === 'skipped' ? '⏭️ Skipped' :
                             reply.masterinbox_sync_status === 'not_applicable' ? '— Not Applicable' :
                             '— Not Synced'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Sync Attempted At:</span>
                          <span className="font-medium">
                            {reply.masterinbox_sync_attempted_at ? new Date(reply.masterinbox_sync_attempted_at).toLocaleString() : 'N/A'}
                          </span>
                        </div>
                        {reply.masterinbox_sync_error && (
                          <div className="flex flex-col gap-1 pt-2 border-t">
                            <span className="text-gray-600 text-sm">Sync Error:</span>
                            <span className="text-red-600 text-xs font-mono bg-red-50 p-2 rounded break-all">
                              {reply.masterinbox_sync_error}
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between pt-2 border-t">
                          <span className="text-gray-600 flex items-center gap-1">
                            <ShieldBan className="w-3.5 h-3.5" />
                            Blocklist Status:
                          </span>
                          {blocklistLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                          ) : blocklistEntry ? (
                            <Badge variant="outline" className={`${
                              blocklistEntry.block_type === 'email_and_domain'
                                ? 'bg-red-100 text-red-800 border-red-300'
                                : 'bg-orange-100 text-orange-800 border-orange-300'
                            }`}>
                              🚫 {blocklistEntry.block_type === 'email_and_domain' ? 'Email + Domain Blocked' : 'Email Blocked'}
                            </Badge>
                          ) : (
                            <span className="font-medium text-gray-500">Not Blocked</span>
                          )}
                        </div>
                        {blocklistEntry && (
                          <>
                            <div className="flex justify-between">
                              <span className="text-gray-600 text-sm">Block Reason:</span>
                              <span className="text-sm text-gray-700">{blocklistEntry.block_reason}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 text-sm">SmartLead Sync:</span>
                              <span className={`text-sm font-medium ${
                                blocklistEntry.smartlead_sync_status === 'success' ? 'text-green-600' :
                                blocklistEntry.smartlead_sync_status === 'failed' ? 'text-red-600' :
                                'text-gray-500'
                              }`}>
                                {blocklistEntry.smartlead_sync_status === 'success' ? '✅ Synced' :
                                 blocklistEntry.smartlead_sync_status === 'failed' ? '❌ Failed' :
                                 blocklistEntry.smartlead_sync_status === 'already_blocked' ? '⏭️ Already Blocked' :
                                 blocklistEntry.smartlead_sync_status || 'N/A'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 text-sm">Expiry:</span>
                              <span className="text-sm text-gray-700">
                                {blocklistEntry.expires_at
                                  ? new Date(blocklistEntry.expires_at).toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})
                                  : '🔐 Permanent'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 text-sm">Blocked At:</span>
                              <span className="text-sm text-gray-700">
                                {new Date(blocklistEntry.created_at).toLocaleString()}
                              </span>
                            </div>
                          </>
                        )}
                        </>)}
                        {reply.categorization_reasoning && (
                          <div className="flex flex-col gap-1 pt-2 border-t">
                            <span className="text-gray-600 text-sm">Reasoning:</span>
                            <span className="text-sm text-gray-700 bg-gray-50 p-2 rounded break-all">
                              {reply.categorization_reasoning}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Campaign Context */}
                    <div className="bg-white border rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-4">Campaign Context</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs text-muted-foreground">Campaign Name</div>
                          <div className="text-sm font-medium">
                            {reply.campaign_name || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Campaign ID</div>
                          <div className="text-sm font-medium">
                            {reply.campaign_id != null ? reply.campaign_id : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Workspace Name</div>
                          <div className="text-sm font-medium">
                            {reply.workspace_name || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Workspace ID</div>
                          <div className="text-sm font-medium">
                            {reply.workspace_id != null ? reply.workspace_id : 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Prospect ID</div>
                          <div className="text-sm font-medium">
                            {reply.prospect_id || 'N/A'}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground">Lead ID</div>
                          <div className="text-sm font-medium">
                            {reply.lead_id || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>


                    {/* Actions Placeholder for Sync, SDR Tracking, Notifications */}
                    <div className="bg-white border rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Follow-up Actions</h4>
                      <div className="flex flex-wrap gap-2">
                        {onSyncToMasterInbox && reply.label_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onSyncToMasterInbox(reply)}
                            disabled={isSyncingToMasterInbox || !reply.prospect_id}
                          >
                            {isSyncingToMasterInbox ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Syncing...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Sync to List
                              </>
                            )}
                          </Button>
                        )}
                        {onTrackSDRResponse && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onTrackSDRResponse(reply)}
                            disabled
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Track SDR Response (Pending Setup)
                          </Button>
                        )}
                        {onSendTestNotification && 
                         reply.reply_category_type && 
                         ['positive', 'negative'].includes(reply.reply_category_type) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleSendTestNotificationClick}
                            disabled={isSendingTestNotification}
                          >
                            {isSendingTestNotification ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Sending Test...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Send Test Notification
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                      {onSendTestNotification && 
                       reply.reply_category_type && 
                       ['positive', 'negative', 'neutral'].includes(reply.reply_category_type) && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          Test notifications are sent to the shared Slack test channel (C0A79JSN4V8).
                        </div>
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="messages" className="space-y-4 w-full">
                    <div className="bg-white border rounded-lg overflow-hidden">
                      {/* Header with descriptive text and categorization */}
                      <div className="border-b p-4 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-muted-foreground">
                            Chronological message thread from Master Inbox API
                          </div>
                          <div className="flex items-center gap-2">
                            {reply.label_name ? (
                              <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                                {reply.label_name}
                              </Badge>
                            ) : null}
                            {reply.sub_label ? (
                              <Badge variant="outline" className="bg-indigo-100 text-indigo-800 border-indigo-300">
                                {reply.sub_label}
                              </Badge>
                            ) : null}
                            {reply.reply_category_type ? (() => {
                              const normalizedType = reply.reply_category_type.toLowerCase()
                              let colorClass = ''
                              
                              // Use same colors as AI Reply Tagger
                              if (normalizedType === 'positive') {
                                colorClass = 'bg-green-100 text-green-800 border-green-300'
                              } else if (normalizedType === 'negative') {
                                colorClass = 'bg-red-100 text-red-800 border-red-300'
                              } else if (normalizedType === 'neutral') {
                                colorClass = 'bg-orange-100 text-orange-800 border-orange-300'
                              } else if (normalizedType === 'auto-reply' || normalizedType === 'autoreply') {
                                colorClass = 'bg-gray-100 text-gray-800 border-gray-300'
                              } else if (normalizedType === 'ooo' || normalizedType === 'out of office') {
                                colorClass = 'bg-purple-100 text-purple-800 border-purple-300'
                              }
                              
                              return (
                                <Badge 
                                  variant="outline"
                                  className={`${colorClass}`}
                                >
                                  {reply.reply_category_type}
                                </Badge>
                              )
                            })() : null}
                            {!reply.label_name && !reply.sub_label && !reply.reply_category_type && (
                              <span className="text-xs text-muted-foreground">Not categorized</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
                        {(() => {
                          // Parse messages from api_response_get_messages
                          let messages: any[] = []
                          
                          try {
                            if (reply.api_response_get_messages && 
                                reply.api_response_get_messages.data && 
                                Array.isArray(reply.api_response_get_messages.data)) {
                              messages = reply.api_response_get_messages.data
                                .map((msg: any, idx: number) => ({
                                  ...msg,
                                  timestamp: msg.date || msg.created_at || 0,
                                  messageId: msg._id || `msg-${msg.date || idx}`
                                }))
                                .sort((a: any, b: any) => a.timestamp - b.timestamp) // Sort oldest to newest
                            }
                          } catch (error) {
                            console.error('Error parsing api_response_get_messages:', error)
                          }

                          if (messages.length === 0) {
                            return (
                              <div className="text-center py-8 text-muted-foreground">
                                <p className="text-sm">No messages available</p>
                                <p className="text-xs mt-1">API response data not yet populated</p>
                              </div>
                            )
                          }

                          // Get current campaign sender identifier for comparison
                          // For email: to_address is the sending email; for LinkedIn: from_address is the sender's LinkedIn URL
                          const isLinkedInChannel = reply.channel_id?.toLowerCase().includes('heyreach')
                          const currentSenderEmail = isLinkedInChannel
                            ? (reply.from_address || '')
                            : (reply.to_address || '')

                          // EDGE CASE: When has_outbound_message=false and there's only reply messages
                          // (no sent:1 messages in the API data), check if the reply contains an embedded
                          // quoted outbound email. If so, split it into two virtual messages.
                          const hasSentMessages = messages.some((m: any) => m.sent === 1)
                          if (!hasSentMessages && messages.length > 0) {
                            // Check the first reply message (chronologically first, which is the original reply)
                            const firstReply = messages[0]
                            const extracted = extractEmbeddedOutbound(firstReply.html || '', firstReply.text || '')

                            if (extracted) {
                              // Clean the quoted outbound HTML: remove <title>, <meta>, <head>, <style> tags
                              // and strip the attribution "wrote:" line that may appear inside the blockquote
                              let cleanedOutboundHtml = extracted.quotedOutboundHtml
                              if (cleanedOutboundHtml) {
                                cleanedOutboundHtml = cleanedOutboundHtml
                                  .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
                                  .replace(/<meta[^>]*\/?>/gi, '')
                                  .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
                                  .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                                  // Remove the "Den ... skrev:" / "On ... wrote:" attribution line inside blockquote
                                  .replace(/(?:Den|On|El|Le|Am)\s+[\s\S]*?\s+(?:skrev|wrote|escribió|a écrit|schrieb)\s*[^:]*:\s*(?:<br\s*\/?>)?\s*/i, '')
                                  .trim()
                              }

                              // Clean the quoted outbound text: remove email headers and attribution lines
                              let cleanedOutboundText = extracted.quotedOutboundText
                              if (cleanedOutboundText) {
                                cleanedOutboundText = cleanedOutboundText
                                  // Remove common email header lines (From:, To:, Date:, Subject:, Sent:)
                                  .replace(/^(From|To|Date|Subject|Sent|Cc|Bcc):.*$/gim, '')
                                  .replace(/^\s*\n/gm, '') // remove blank lines left behind
                                  .trim()
                              }

                              // Create a synthetic outbound message from the quoted content
                              const syntheticOutbound = {
                                ...firstReply,
                                sent: 1,
                                subject: '', // Clear subject — it's the reply's "Re:" subject, not needed on outbound bubble
                                from_address: reply.to_address || '', // sender is the outreach person
                                from_name: reply.to_name || '',
                                to_address: reply.from_address || reply.email || '',
                                to_name: reply.name || reply.from_name || '',
                                html: cleanedOutboundHtml || '',
                                text: cleanedOutboundText || '',
                                messageId: `${firstReply.messageId || 'msg-0'}-extracted-outbound`,
                                _isExtractedOutbound: true,
                                _extractedSenderInfo: extracted.quotedSenderInfo,
                              }

                              // Update the original reply to only contain the actual reply part
                              const cleanedReply = {
                                ...firstReply,
                                html: extracted.replyHtml || '',
                                text: extracted.replyText || (extracted.replyHtml ? '' : firstReply.text),
                                messageId: firstReply.messageId || 'msg-0',
                              }
                              // If we got replyHtml, clear text so HTML takes priority
                              // If we got replyText, clear html so text takes priority
                              if (extracted.replyHtml) {
                                cleanedReply.text = ''
                              } else if (extracted.replyText) {
                                cleanedReply.html = ''
                              }

                              // Replace messages: outbound first (chronologically older), then cleaned reply
                              messages = [syntheticOutbound, cleanedReply, ...messages.slice(1)]
                            }
                          }

                          return messages.map((msg: any, index: number) => {
                            const isSent = msg.sent === 1
                            const isOriginalMessage = index === 0 // First message in chronological order is the original
                            const isSecondMessage = index === 1 // Second message
                            const isThirdMessage = index === 2 // Third message
                            const isLinkedIn = reply.channel_id?.toLowerCase().includes('heyreach')
                            const senderName = isLinkedIn
                              ? (msg.from_name || (isSent
                                  ? (reply.from_name || 'Unknown')
                                  : (reply.to_name || reply.name || 'Unknown')))
                              : (msg.from_name || 'Unknown')
                            const senderEmail = isLinkedIn
                              ? (msg.from_address || (isSent
                                  ? (reply.from_address || '')
                                  : (reply.to_address || reply.linkedin || '')))
                              : (msg.from_address || '')
                            
                            // Check if this sent message is from a different campaign/sender
                            const isDifferentSender = isSent && currentSenderEmail && senderEmail && 
                              senderEmail.toLowerCase() !== currentSenderEmail.toLowerCase()
                            
                            const timestamp = msg.time || (msg.date ? new Date(msg.date).toLocaleString() : '')
                            const subject = msg.subject || ''
                            const htmlContent = msg.html || ''
                            const textContent = msg.text || ''
                            const messageId = msg.messageId || `msg-${index}`
                            const isCollapsed = collapsedMessages.has(messageId)
                            
                            // Sanitize HTML for safe rendering — strip scripts, event handlers, iframes
                            const sanitizeHtml = (html: string) => {
                              return html
                                .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                                .replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi, '')
                                .replace(/<object[^>]*>[\s\S]*?<\/object>/gi, '')
                                .replace(/<embed[^>]*>/gi, '')
                                .replace(/<link[^>]*>/gi, '')
                                .replace(/<meta[^>]*>/gi, '')
                                .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
                                .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
                                .replace(/\son\w+\s*=\s*["'][^"']*["']/gi, '') // remove event handlers
                                .replace(/javascript\s*:/gi, '') // remove javascript: URIs
                            }

                            // Determine content and rendering mode
                            const hasHtml = !!htmlContent
                            const safeHtml = hasHtml ? sanitizeHtml(htmlContent) : ''
                            const formattedContent = hasHtml ? safeHtml : (textContent || '').trim()
                            const contentLength = hasHtml ? safeHtml.replace(/<[^>]*>/g, '').length : formattedContent.length

                            // For collapsed state, show plain text preview
                            const plainTextPreview = hasHtml
                              ? safeHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
                              : formattedContent
                            const displayContent = isCollapsed && contentLength > 200
                              ? plainTextPreview.substring(0, 200) + '...'
                              : formattedContent

                            const toggleCollapse = () => {
                              setCollapsedMessages(prev => {
                                const newSet = new Set(prev)
                                if (newSet.has(messageId)) {
                                  newSet.delete(messageId)
                                } else {
                                  newSet.add(messageId)
                                }
                                return newSet
                              })
                            }

                            return (
                              <div
                                key={messageId}
                                className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
                              >
                                <div className={`max-w-[75%] ${isSent ? 'flex flex-col items-end' : 'flex flex-col items-start'}`}>
                                  <div
                                    className={`rounded-lg p-4 w-full ${
                                      isDifferentSender
                                        ? isSent
                                          ? 'bg-amber-50 border-2 border-amber-300'
                                          : 'bg-green-50 border border-green-200'
                                        : isOriginalMessage
                                          ? isSent
                                            ? 'bg-blue-100 border-2 border-blue-300'
                                            : 'bg-green-100 border-2 border-green-300'
                                          : isSent
                                            ? 'bg-blue-50 border border-blue-200'
                                            : 'bg-green-50 border border-green-200'
                                    }`}
                                  >
                                    {isDifferentSender && (
                                      <div className="mb-2 pb-2 border-b border-amber-200">
                                        <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                                          Previous Campaign / Different Sender
                                        </Badge>
                                      </div>
                                    )}
                                    {msg._isExtractedOutbound && (
                                      <div className="mb-2 pb-2 border-b border-blue-200">
                                        <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 text-xs">
                                          📧 Extracted from quoted reply
                                        </Badge>
                                        {msg._extractedSenderInfo && (
                                          <span className="ml-2 text-xs text-muted-foreground">{msg._extractedSenderInfo}</span>
                                        )}
                                      </div>
                                    )}
                                    {subject && (
                                      <div className={`text-sm font-semibold text-gray-900 mb-3 pb-2 border-b ${isOriginalMessage ? 'border-gray-300' : 'border-gray-200'}`}>
                                        {subject}
                                        {isOriginalMessage && (
                                          <span className="ml-2 text-xs font-normal text-muted-foreground">
                                            (Original Message)
                                          </span>
                                        )}
                                      </div>
                                    )}
                                    {/* Sender Details and Timestamp - Same row with right-aligned timestamp */}
                                    <div className={`mb-3 pb-2 border-b ${
                                      isSecondMessage 
                                        ? isSent
                                          ? 'border-blue-200 bg-blue-50/30 px-2 py-1 rounded'
                                          : 'border-green-200 bg-green-50/30 px-2 py-1 rounded'
                                        : isThirdMessage 
                                          ? isSent
                                            ? 'border-blue-200 bg-blue-50/30 px-2 py-1 rounded'
                                            : 'border-green-200 bg-green-50/30 px-2 py-1 rounded'
                                          : 'border-gray-100'
                                    }`}>
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                          <span className={`text-sm font-semibold ${
                                            isSent 
                                              ? 'text-gray-900' 
                                              : isSecondMessage 
                                                ? 'text-blue-900' 
                                                : isThirdMessage 
                                                  ? 'text-green-900' 
                                                  : 'text-gray-900'
                                          }`}>
                                            {senderName}
                                          </span>
                                          {senderEmail && (
                                            <span className={`text-xs ${
                                              isSent 
                                                ? 'text-muted-foreground' 
                                                : isSecondMessage 
                                                  ? 'text-blue-700' 
                                                  : isThirdMessage 
                                                    ? 'text-green-700' 
                                                    : 'text-muted-foreground'
                                            }`}>
                                              ({senderEmail})
                                            </span>
                                          )}
                                        </div>
                                        {timestamp && (
                                          <span className={`text-xs ${
                                            isSent 
                                              ? 'text-muted-foreground' 
                                              : isSecondMessage 
                                                ? 'text-blue-700' 
                                                : isThirdMessage 
                                                  ? 'text-green-700' 
                                                  : 'text-muted-foreground'
                                          }`}>
                                            {timestamp}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {/* Message Content */}
                                    {isCollapsed || !hasHtml ? (
                                      <div className={`text-sm leading-relaxed whitespace-pre-wrap break-words pt-2 text-gray-900`} style={{ lineHeight: '1.6' }}>
                                        {displayContent || 'No content'}
                                      </div>
                                    ) : (
                                      <div
                                        className="text-sm pt-2 text-gray-900 break-words [&_img]:max-w-full [&_img]:h-auto [&_a]:text-blue-600 [&_a]:underline [&_table]:border-collapse [&_td]:p-1 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600"
                                        style={{ lineHeight: '1.6', overflowWrap: 'break-word' }}
                                        dangerouslySetInnerHTML={{ __html: displayContent }}
                                      />
                                    )}
                                  </div>
                                  {contentLength > 200 && (
                                    <button
                                      onClick={toggleCollapse}
                                      className="mt-1 flex items-center justify-center w-full text-muted-foreground hover:text-gray-900 transition-colors"
                                      aria-label={isCollapsed ? 'Expand message' : 'Collapse message'}
                                    >
                                      {isCollapsed ? (
                                        <ChevronDown className="w-4 h-4" />
                                      ) : (
                                        <ChevronUp className="w-4 h-4" />
                                      )}
                                    </button>
                                  )}
                                </div>
                              </div>
                            )
                          })
                        })()}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="other" className="space-y-4 w-full">
                    <div className="bg-white border rounded-lg p-4 flex flex-col h-[60vh]">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <RefreshCw className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-medium">Raw get-messages API Response</span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-auto bg-muted rounded-md p-3">
                        <pre className="text-xs whitespace-pre-wrap break-words">
                          {reply.api_response_get_messages
                            ? (() => {
                                try {
                                  return JSON.stringify(reply.api_response_get_messages, null, 2)
                                } catch (e) {
                                  return '// Error parsing api_response_get_messages'
                                }
                              })()
                            : '// api_response_get_messages is empty for this reply'}
                        </pre>
                      </div>
                    </div>
                  </TabsContent>
                </div>
              </ScrollArea>
            </div>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  )
}

